import { hasAuthToken } from './authToken'

/**
 * Session expiration handler - SECURE: stores only expiresAt (Unix timestamp), never the token.
 * Token stays in HttpOnly cookies; XSS cannot steal it.
 * expiresAt is kept in localStorage so proactive expiry / refresh-on-load still work after the browser is closed (matches 24h cookie session).
 */

const SESSION_EXPIRES_AT_KEY = 'eclipsera_session_expires_at'
const SESSION_WALLET_KEY = 'eclipsera_session_wallet'
/** Must match `contexts/web3Context` virtual wallet prefix */
const VIRTUAL_WALLET_PREFIX = 'eclip_vw_'
const PENDING_REAUTH_AFTER_SESSION_KEY = 'eclipsera_pending_reauth_after_session'
/** Set when user taps Enter on /start so `/` (main game) is allowed until the tab/window closes. */
const GAME_ROOT_ALLOWED_KEY = 'eclipsera_ok_main_root_this_tab'
/** Set when user taps Start on /start so GamePage can open the news modal after navigation. */
const PENDING_NEWS_AFTER_START_KEY = 'eclipsera_pending_news_after_start'

export const markNewsModalPendingAfterStart = (): void => {
  try {
    sessionStorage.setItem(PENDING_NEWS_AFTER_START_KEY, '1')
  } catch {
    /* noop */
  }
}

export const peekNewsModalPendingAfterStart = (): boolean => {
  try {
    return sessionStorage.getItem(PENDING_NEWS_AFTER_START_KEY) === '1'
  } catch {
    return false
  }
}

export const clearNewsModalPendingAfterStart = (): void => {
  try {
    sessionStorage.removeItem(PENDING_NEWS_AFTER_START_KEY)
  } catch {
    /* noop */
  }
}

export const markGameRootEntryAllowed = (): void => {
  try {
    sessionStorage.setItem(GAME_ROOT_ALLOWED_KEY, '1')
  } catch {
    /* noop */
  }
}

export const isGameRootEntryAllowed = (): boolean => {
  try {
    return sessionStorage.getItem(GAME_ROOT_ALLOWED_KEY) === '1'
  } catch {
    return false
  }
}

export const clearGameRootEntryAllowed = (): void => {
  try {
    sessionStorage.removeItem(GAME_ROOT_ALLOWED_KEY)
  } catch {
    /* noop */
  }
}

/** Read expiry from localStorage, migrating legacy sessionStorage (cleared when browser closes). */
function readStoredExpiresAtRaw(): string | null {
  try {
    const fromLs = localStorage.getItem(SESSION_EXPIRES_AT_KEY)
    if (fromLs) return fromLs
    const fromSs = sessionStorage.getItem(SESSION_EXPIRES_AT_KEY)
    if (fromSs) {
      localStorage.setItem(SESSION_EXPIRES_AT_KEY, fromSs)
      sessionStorage.removeItem(SESSION_EXPIRES_AT_KEY)
      return fromSs
    }
    return null
  } catch {
    return null
  }
}

type SessionExpiredCallback = () => void

let onSessionExpired: SessionExpiredCallback | null = null

/** Clear tab-scoped `eclipsera_*` sessionStorage keys after logout / session reset */
export const clearEclipseraSessionStorageKeys = (): void => {
  try {
    const keys: string[] = []
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i)
      if (k && k.startsWith('eclipsera_')) keys.push(k)
    }
    keys.forEach((k) => sessionStorage.removeItem(k))
  } catch {
    /* noop */
  }
}

export const setSessionExpiredHandler = (callback: SessionExpiredCallback | null): void => {
  onSessionExpired = callback
}

/** Email / virtual-wallet users must sign in again — do not send them to /start “set nickname” with stale identity. */
export const hasPendingReauthAfterSessionExpiry = (): boolean => {
  try {
    return sessionStorage.getItem(PENDING_REAUTH_AFTER_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

/** Email / virtual-wallet users must sign in again — do not send them to /start “set nickname” with stale identity. */
export const markPendingReauthAfterSessionExpiry = (): void => {
  try {
    sessionStorage.setItem(PENDING_REAUTH_AFTER_SESSION_KEY, '1')
  } catch {
    /* noop */
  }
}

export const clearPendingReauthAfterSessionExpiry = (): void => {
  try {
    sessionStorage.removeItem(PENDING_REAUTH_AFTER_SESSION_KEY)
  } catch {
    /* noop */
  }
}

export const triggerSessionExpired = (): void => {
  markPendingReauthAfterSessionExpiry()
  if (onSessionExpired) {
    onSessionExpired()
  }
}

/** Store session expiration timestamp (Unix seconds) - safe, no token exposure */
export const setSessionExpiresAt = (expiresAt: number): void => {
  try {
    localStorage.setItem(SESSION_EXPIRES_AT_KEY, String(expiresAt))
    sessionStorage.removeItem(SESSION_EXPIRES_AT_KEY)
  } catch {
    // storage full or unavailable
  }
}

/** Check if session is expired using stored timestamp - no token read */
export const isSessionExpiredByTimestamp = (): boolean => {
  try {
    const stored = readStoredExpiresAtRaw()
    if (!stored) return false
    const expiresAt = parseInt(stored, 10)
    if (isNaN(expiresAt)) return false
    return Date.now() >= expiresAt * 1000
  } catch {
    return false
  }
}

/** Email / virtual-wallet session persisted in storage (mirrors `hasPersistedEmailSessionWallet` without importing React context). */
export const hasPersistedVirtualSessionWallet = (): boolean => {
  if (typeof window === 'undefined') return false
  try {
    const raw =
      localStorage.getItem(SESSION_WALLET_KEY) ||
      sessionStorage.getItem(SESSION_WALLET_KEY)
    return !!raw && raw.startsWith(VIRTUAL_WALLET_PREFIX)
  } catch {
    return false
  }
}

/** Whether we have an active session (expiresAt was set) */
export const hasSessionExpiresAt = (): boolean => {
  try {
    return !!readStoredExpiresAtRaw()
  } catch {
    return false
  }
}

/** Clear stored expiration (on logout) */
export const clearSessionExpiresAt = (): void => {
  try {
    localStorage.removeItem(SESSION_EXPIRES_AT_KEY)
    sessionStorage.removeItem(SESSION_EXPIRES_AT_KEY)
  } catch {
    /* noop */
  }
}

/**
 * True when the player has signed in with email/Google (or has a valid session window),
 * as opposed to only connecting Phantom without an account session.
 */
export function hasEstablishedGameSession(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (hasAuthToken()) return true
    if (hasPersistedVirtualSessionWallet()) return true
    return hasSessionExpiresAt() && !isSessionExpiredByTimestamp()
  } catch {
    return false
  }
}

/**
 * Older clients wrote `expiresAt` from any API response; a quest/building timestamp in the past made
 * proactive expiry think the session was dead. Drop only clearly garbage values.
 */
export const clearCorruptSessionExpiryTimestamp = (): void => {
  try {
    const raw = localStorage.getItem(SESSION_EXPIRES_AT_KEY)
    if (!raw) return
    const sec = parseInt(raw, 10)
    if (Number.isNaN(sec) || sec <= 0) {
      clearSessionExpiresAt()
      return
    }
    const now = Math.floor(Date.now() / 1000)
    if (sec < now - 60 * 86400 || sec > now + 14 * 86400) {
      clearSessionExpiresAt()
    }
  } catch {
    /* noop */
  }
}
